import { call, put, takeLatest, takeEvery, take, select, fork, cancel } from 'redux-saga/effects'
import type { Task as SagaTask } from 'redux-saga'
import type { PayloadAction } from '@reduxjs/toolkit'
import { collection, query, where, doc, updateDoc, setDoc, deleteDoc, serverTimestamp, runTransaction, getDoc, getDocs, limit as fsLimit, Timestamp, arrayUnion } from 'firebase/firestore'
import { createSnapshotChannel } from '../utils/createSnapshotChannel'
import { showToast } from '@/components/app/Toast/showToast'
import { db } from '../../lib/firebase'
import { AnalyticsEvent, trackEvent } from '@/lib/analytics'
import { selectIsWorkspacePro, selectStatuses, selectWorkspace } from '../workspace/workspaceSelectors'
import { selectAuth } from '../auth/authSelectors'
import type { RootState } from '../index'
import {
    createTaskSaga,
    createTasksSaga,
    updateTaskSaga,
    updateTaskDescriptionSaga,
    deleteTaskSaga,
    reorderTaskSaga,
    moveTaskToListSaga,
    toggleCompleteSaga,
    subscribeDescriptionSaga,
    unsubscribeDescriptionSaga,
    prefetchDescriptionSaga,
    fetchTaskByNumberSaga,
    setDeepLinkFetchPending,
    upsertTasks,
    removeTaskIdsScoped,
    clearOptimistic,
    clearTasks,
    addOptimistic,
    removeOptimistic,
    addTaskLocal,
    updateTaskLocal,
    removeTaskLocal,
    setLoading,
    setError,
    setCompletedInitialized,
} from './tasksSlice'
import { incrementTaskCounter } from '../workspace/workspaceSlice'
import { upsertProject, type Project } from '../projects/projectsSlice'
import { selectAllTasks, selectTasksOptimistic, selectTasksAllIds, selectTaskById } from './tasksSelectors'
import { logTiming } from '@/utils/timing'
import type { Task } from '../../contexts/TasksContext'
import type { Status } from '../../contexts/WorkspaceContext'
import {setOpenModal} from "@/store/ui/uiSlice.ts";
import {FREE_PLAN_TASKS_LIMIT} from "@/const/constants.ts";

function* createTaskWorker(action: PayloadAction<{
    listId: string
    name: string
    projectId: string
    workspaceId: string
    userId: string
    description?: unknown
}>): Generator<any, void, any> {
    const { listId, name, projectId, workspaceId, userId, description } = action.payload

    const isProPlan: boolean = yield select(selectIsWorkspacePro)
    const taskIds: string[] = yield select(selectTasksAllIds)
    const totalTaskCount = taskIds.length
    if (!isProPlan && totalTaskCount >= FREE_PLAN_TASKS_LIMIT) {
        // Paywall funnel: blocked intent. Authed users only — a guest workspace has
        // no plan to upgrade, so a guest hit would be noise in the funnel.
        const authed = yield select(selectAuth)
        if (authed) trackEvent(AnalyticsEvent.LimitHit, { limit: 'tasks', workspace_id: workspaceId })
        yield put(setOpenModal({ type: 'upgradeToPro' }))
        return
    }

    const tasks: Task[] = yield select(selectAllTasks)
    const optimistic: Record<string, Task> = yield select(selectTasksOptimistic)
    const allTasks = [...tasks, ...Object.values(optimistic)]
    const listTasks = allTasks.filter(t => t.listId === listId)
    const maxOrder = listTasks.reduce((max: number, task: Task) => Math.max(max, task.order), -1)

    const tempId = `temp-${Date.now()}`
    const optimisticTask: Task = {
        id: tempId,
        workspaceId,
        projectId,
        listId,
        number: 0,
        name,
        description: description ?? undefined,
        ownerId: userId,
        assigneeId: null,
        statusId: '',
        labels: [],
        order: maxOrder + 1,
        isCompleted: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        pending: true
    }

    yield put(addOptimistic({ tempId, task: optimisticTask }))

    // Guest mode: create task locally
    const authState = yield select(selectAuth)
    if (!authState) {
        const workspace = yield select(selectWorkspace)
        const statuses = yield select(selectStatuses)
        const counter = (workspace?.taskCounter || 0) + 1
        yield put(incrementTaskCounter())
        yield put(addTaskLocal({
            ...optimisticTask,
            id: `task-${Date.now()}`,
            number: counter,
            statusId: statuses[0]?.id || '',
            pending: false,
        }))
        yield put(removeOptimistic(tempId))
        showToast('Task created')
        return
    }

    try {
        yield call(runTransaction, db, async (transaction: any) => {
            const workspaceRef = doc(db, 'workspaces', workspaceId)
            const workspaceDoc = await transaction.get(workspaceRef)

            if (!workspaceDoc.exists()) {
                throw new Error('Workspace not found')
            }

            const currentCounter = workspaceDoc.data().taskCounter || 0
            const newNumber = currentCounter + 1

            transaction.update(workspaceRef, { taskCounter: newNumber })

            const taskRef = doc(collection(db, 'tasks'))
            transaction.set(taskRef, {
                workspaceId,
                projectId,
                listId,
                number: newNumber,
                name,
                ownerId: userId,
                assigneeId: null,
                statusId: '',
                labels: [],
                order: maxOrder + 1,
                isCompleted: false,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            })
            // Description lives in a separate collection (kept out of the eager tasks
            // snapshot). Only write it when the task is created with one.
            if (description) {
                transaction.set(doc(db, 'task_descriptions', taskRef.id), {
                    taskId: taskRef.id,
                    workspaceId,
                    projectId,
                    content: JSON.parse(JSON.stringify(description)),
                    updatedAt: serverTimestamp(),
                    updatedBy: userId
                })
            }
        })
        // Activation funnel: task created. PostHog uses the first occurrence per
        // user for the funnel step.
        trackEvent(AnalyticsEvent.TaskCreated, { project_id: projectId })
        showToast('Task created')
    } catch (err) {
        console.error('Error creating task:', err)
        // The optimistic task is removed below, so without this the typed task just
        // vanishes with no explanation (e.g. offline / transaction failure). (SCD-118)
        yield put(removeOptimistic(tempId))
        showToast("Couldn't create the task. Please try again.", { type: 'error' })
    }
}

// Batch create (SCD-897): one task per name in a single counter transaction. Used by the
// add-task input when a bullet / multi-line list is pasted. Optimistically inserts all names
// with incrementing order, then writes them in one transaction (numbers currentCounter+1..+N).
function* createTasksWorker(action: PayloadAction<{ names: string[]; listId: string; projectId: string; workspaceId: string; userId: string }>): Generator<any, void, any> {
    const { names, listId, projectId, workspaceId, userId } = action.payload
    if (names.length === 0) return

    const isProPlan: boolean = yield select(selectIsWorkspacePro)
    const taskIds: string[] = yield select(selectTasksAllIds)
    if (!isProPlan && taskIds.length + names.length > FREE_PLAN_TASKS_LIMIT) {
        const authed = yield select(selectAuth)
        if (authed) trackEvent(AnalyticsEvent.LimitHit, { limit: 'tasks', workspace_id: workspaceId })
        yield put(setOpenModal({ type: 'upgradeToPro' }))
        return
    }

    const tasks: Task[] = yield select(selectAllTasks)
    const optimistic: Record<string, Task> = yield select(selectTasksOptimistic)
    const allTasks = [...tasks, ...Object.values(optimistic)]
    const listTasks = allTasks.filter(t => t.listId === listId)
    const maxOrder = listTasks.reduce((max: number, task: Task) => Math.max(max, task.order), -1)

    const now = Timestamp.now()
    const tempIds = names.map((_, i) => `temp-${Date.now()}-${i}`)
    for (let i = 0; i < names.length; i++) {
        yield put(addOptimistic({
            tempId: tempIds[i],
            task: {
                id: tempIds[i],
                workspaceId,
                projectId,
                listId,
                number: 0,
                name: names[i],
                description: undefined,
                ownerId: userId,
                assigneeId: null,
                statusId: '',
                labels: [],
                order: maxOrder + 1 + i,
                isCompleted: false,
                createdAt: now,
                updatedAt: now,
                pending: true
            }
        }))
    }

    // Guest mode: create tasks locally.
    const authState = yield select(selectAuth)
    if (!authState) {
        const workspace = yield select(selectWorkspace)
        const statuses = yield select(selectStatuses)
        let counter = workspace?.taskCounter || 0
        for (let i = 0; i < names.length; i++) {
            counter += 1
            yield put(incrementTaskCounter())
            yield put(addTaskLocal({
                id: `task-${Date.now()}-${i}`,
                workspaceId,
                projectId,
                listId,
                number: counter,
                name: names[i],
                description: undefined,
                ownerId: userId,
                assigneeId: null,
                statusId: statuses[0]?.id || '',
                labels: [],
                order: maxOrder + 1 + i,
                isCompleted: false,
                createdAt: now,
                updatedAt: now,
                pending: false
            }))
            yield put(removeOptimistic(tempIds[i]))
        }
        showToast(`${names.length} tasks created`)
        return
    }

    try {
        yield call(runTransaction, db, async (transaction: any) => {
            const workspaceRef = doc(db, 'workspaces', workspaceId)
            const workspaceDoc = await transaction.get(workspaceRef)
            if (!workspaceDoc.exists()) {
                throw new Error('Workspace not found')
            }
            const currentCounter = workspaceDoc.data().taskCounter || 0
            transaction.update(workspaceRef, { taskCounter: currentCounter + names.length })
            names.forEach((name, i) => {
                const taskRef = doc(collection(db, 'tasks'))
                transaction.set(taskRef, {
                    workspaceId,
                    projectId,
                    listId,
                    number: currentCounter + 1 + i,
                    name,
                    ownerId: userId,
                    assigneeId: null,
                    statusId: '',
                    labels: [],
                    order: maxOrder + 1 + i,
                    isCompleted: false,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                })
            })
        })
        // One TaskCreated per task, matching single create — so a pasted batch isn't undercounted
        // in the activation funnel (SCD-904).
        for (let i = 0; i < names.length; i++) {
            trackEvent(AnalyticsEvent.TaskCreated, { project_id: projectId })
        }
        showToast(`${names.length} tasks created`)
    } catch (err) {
        console.error('Error creating tasks:', err)
        for (const tempId of tempIds) yield put(removeOptimistic(tempId))
        showToast("Couldn't create the tasks. Please try again.", { type: 'error' })
    }
}

export function* updateTaskWorker(action: PayloadAction<{ taskId: string; data: Partial<Task> }>): Generator<any, void, any> {
    const { taskId, data } = action.payload

    const authState = yield select(selectAuth)
    if (!authState) {
        const localUpdate: Record<string, unknown> = { ...data }
        if (data.statusId) {
            const statuses = yield select(selectStatuses)
            const status = statuses.find((s: any) => s.id === data.statusId)
            if (status) localUpdate.isCompleted = status.systemType === 'done'
        }
        yield put(updateTaskLocal({ taskId, data: localUpdate as Partial<Task> }))
        return
    }

    try {
        // Derive the completion flag from the target status's systemType up front, off the
        // statuses already in the store. The SAME value feeds both the optimistic local update
        // and the persisted write below, so they can never disagree.
        let derivedCompleted: boolean | undefined
        // undefined = don't touch the field; string|null = value to persist. When a status change
        // moves the task into Done we stamp the pre-change status so a later checkbox-uncomplete can
        // restore it; an explicit move to an open status clears it (the user chose the status). (SCD-982)
        let previousStatusIdWrite: string | null | undefined
        // Analytics: whether this update transitions the task open → completed. Decided from the
        // pre-optimistic state (the local update below mutates isCompleted), fired only after the
        // Firestore write succeeds so a failed write can't produce a phantom completion.
        let becameCompleted = false
        let completedWorkspaceId: string | undefined
        if (data.statusId) {
            const statuses = yield select(selectStatuses)
            const status = statuses.find((s: any) => s.id === data.statusId)
            if (status) {
                derivedCompleted = status.systemType === 'done'
                // Capture the pre-change status BEFORE the optimistic update overwrites it below.
                const existing: Task | undefined = yield select((state: RootState) => selectTaskById(state, taskId))
                becameCompleted = derivedCompleted && existing !== undefined && !existing.isCompleted
                completedWorkspaceId = existing?.workspaceId
                const prevStatus = existing ? statuses.find((s: Status) => s.id === existing.statusId) : undefined
                if (derivedCompleted) {
                    // Remember where it came from, unless it was already a Done status.
                    if (existing && prevStatus && prevStatus.systemType !== 'done') previousStatusIdWrite = existing.statusId
                } else {
                    previousStatusIdWrite = null
                }
                // Optimistically reflect the status change locally (including isCompleted)
                // BEFORE the async Firestore write. Otherwise switching to a "done" status writes
                // isCompleted=true to Firestore first; the open-tasks listener (isCompleted == false)
                // then fires a 'removed' while the store still has isCompleted=false, so
                // removeTaskIdsScoped drops the task from the store — and the open task page, finding
                // no task, redirects away (SCD-696).
                const localData: Partial<Task> = { statusId: data.statusId, isCompleted: derivedCompleted }
                if (previousStatusIdWrite !== undefined) localData.previousStatusId = previousStatusIdWrite
                yield put(updateTaskLocal({ taskId, data: localData }))
            }
        }

        const cleanData = JSON.parse(JSON.stringify(data))
        // Description lives in the separate task_descriptions collection and is written by the
        // dedicated updateTaskDescriptionSaga / updateTaskDescriptionWorker (takeEvery), NOT here:
        // this worker is takeLatest, so a later field update would cancel an in-flight description
        // setDoc and lose the body (SCD-905). Strip it out so it never lands on the task doc.
        const { description: _description, ...rest } = cleanData

        const hasOtherFields = Object.keys(rest).length > 0
        if (hasOtherFields) {
            // Reset connector markers: a UI edit is performed by a real user, so clear any
            // sticky viaMcp/apiKeyName left by a previous MCP/API write (otherwise the
            // activity event would be wrongly badged as MCP/API).
            const updateData: Record<string, unknown> = { ...rest, updatedAt: serverTimestamp(), updatedBy: authState.id, viaMcp: null, apiKeyName: null }

            // Persist isCompleted in lockstep with the status, using the value derived above.
            // Do NOT re-read the task's statusId from the store to decide whether to write it —
            // the optimistic update just set it to the new status, so a guard like
            // `data.statusId !== task.statusId` always sees "no change" and skips the write,
            // leaving isCompleted=false on a task that just moved into a Done status (SCD-930).
            if (data.statusId && derivedCompleted !== undefined) {
                updateData.isCompleted = derivedCompleted
            }
            // Persist the restore target alongside the status change (SCD-982).
            if (previousStatusIdWrite !== undefined) {
                updateData.previousStatusId = previousStatusIdWrite
            }

            const taskRef = doc(db, 'tasks', taskId)
            yield call(() => updateDoc(taskRef, updateData))

            if (becameCompleted) {
                trackEvent(AnalyticsEvent.TaskCompleted, { workspace_id: completedWorkspaceId })
            }

            // When assigning someone, automatically add them to project shared_members
            if (data.assigneeId) {
                const task: Task | undefined = yield select((state: RootState) => selectTaskById(state, taskId))
                if (task?.projectId) {
                    const projectRef = doc(db, 'projects', task.projectId)
                    yield call(() => updateDoc(projectRef, { shared_members: arrayUnion(data.assigneeId) }))
                }
            }
        }
    } catch (err) {
        console.error('Error updating task:', err)
    }
}

// Description-only save — its OWN takeEvery worker (not updateTaskWorker, which is takeLatest).
// A field update dispatched right after a description edit (e.g. changing the assignee just after
// typing) used to cancel the in-flight description write mid-setDoc, silently losing the body
// (SCD-905). takeEvery here means a later task-doc update can never cancel this. The description
// lives in the separate task_descriptions collection; a description-only edit never touches the
// task doc, so it fires no onTaskUpdated / task.updated webhook.
function* updateTaskDescriptionWorker(action: PayloadAction<{ taskId: string; description: unknown }>): Generator<any, void, any> {
    const { taskId, description } = action.payload

    const authState = yield select(selectAuth)
    if (!authState) {
        yield put(updateTaskLocal({ taskId, data: { description } as Partial<Task> }))
        return
    }

    try {
        const task: Task | undefined = yield select((state: RootState) => selectTaskById(state, taskId))
        // Without the task we can't supply workspaceId/projectId; writing nulls would be
        // rejected by the task_descriptions rules anyway. Skip rather than write garbage.
        if (!task) {
            console.error('Cannot save description: task not found in store', taskId)
            return
        }
        const content = JSON.parse(JSON.stringify(description ?? ''))
        yield call(() => setDoc(doc(db, 'task_descriptions', taskId), {
            taskId,
            workspaceId: task.workspaceId,
            projectId: task.projectId,
            content,
            updatedAt: serverTimestamp(),
            updatedBy: authState.id
        }, { merge: true }))
    } catch (err) {
        console.error('Error saving task description:', err)
    }
}

function* deleteTaskWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const taskId = action.payload

    const authState = yield select(selectAuth)
    if (!authState) {
        yield put(removeTaskLocal(taskId))
        showToast('Task deleted')
        return
    }

    try {
        const taskRef = doc(db, 'tasks', taskId)
        yield call(deleteDoc, taskRef)
        showToast('Task deleted')
    } catch (err) {
        console.error('Error deleting task:', err)
    }
}

function* reorderTaskWorker(action: PayloadAction<{ taskId: string; newOrder: number }>): Generator<any, void, any> {
    const { taskId, newOrder } = action.payload

    try {
        const tasks: Task[] = yield select(selectAllTasks)
        const task = tasks.find(t => t.id === taskId)
        if (!task) return

        const listTasks = tasks
            .filter(t => t.listId === task.listId && !t.isCompleted)
            .sort((a, b) => a.order - b.order)

        // newOrder is an array index — rebuild ordering by splicing task to new position
        const withoutTask = listTasks.filter(t => t.id !== taskId)
        withoutTask.splice(newOrder, 0, task)

        const authState = yield select(selectAuth)

        if (!authState) {
            for (const [index, t] of withoutTask.entries()) {
                if (t.order !== index) {
                    yield put(updateTaskLocal({ taskId: t.id, data: { order: index } }))
                }
            }
            return
        }

        for (const [index, t] of withoutTask.entries()) {
            if (t.order !== index) {
                yield put(updateTaskLocal({ taskId: t.id, data: { order: index } }))
            }
        }

        const updates: Promise<void>[] = []
        withoutTask.forEach((t, index) => {
            if (t.order !== index) {
                const taskRef = doc(db, 'tasks', t.id)
                updates.push(updateDoc(taskRef, { order: index, updatedAt: serverTimestamp() }))
            }
        })

        yield call(() => Promise.all(updates))
    } catch (err) {
        console.error('Error reordering task:', err)
        showToast('Failed to save order. Please try again.', { type: 'error' })
    }
}

function* moveTaskToListWorker(action: PayloadAction<{ taskId: string; targetListId: string; newOrder: number }>): Generator<any, void, any> {
    const { taskId, targetListId, newOrder } = action.payload

    try {
        const tasks: Task[] = yield select(selectAllTasks)
        const task = tasks.find(t => t.id === taskId)
        if (!task) return

        const sourceListId = task.listId

        const sourceListTasks = tasks
            .filter(t => t.listId === sourceListId && !t.isCompleted && t.id !== taskId)
            .sort((a, b) => a.order - b.order)

        // newOrder is an array index in the target list — splice task in at that position
        const targetListTasks = tasks
            .filter(t => t.listId === targetListId && !t.isCompleted)
            .sort((a, b) => a.order - b.order)
        targetListTasks.splice(newOrder, 0, task)

        const authState = yield select(selectAuth)
        if (!authState) {
            for (const [index, t] of sourceListTasks.entries()) {
                if (t.order !== index) {
                    yield put(updateTaskLocal({ taskId: t.id, data: { order: index } }))
                }
            }
            for (const [index, t] of targetListTasks.entries()) {
                if (t.order !== index || t.id === taskId) {
                    yield put(updateTaskLocal({ taskId: t.id, data: { order: index, ...(t.id === taskId ? { listId: targetListId } : {}) } }))
                }
            }
            return
        }

        for (const [index, t] of sourceListTasks.entries()) {
            if (t.order !== index) {
                yield put(updateTaskLocal({ taskId: t.id, data: { order: index } }))
            }
        }
        for (const [index, t] of targetListTasks.entries()) {
            if (t.order !== index || t.id === taskId) {
                yield put(updateTaskLocal({ taskId: t.id, data: { order: index, ...(t.id === taskId ? { listId: targetListId } : {}) } }))
            }
        }

        const updates: Promise<void>[] = []

        sourceListTasks.forEach((t, index) => {
            if (t.order !== index) {
                const taskRef = doc(db, 'tasks', t.id)
                updates.push(updateDoc(taskRef, { order: index, updatedAt: serverTimestamp() }))
            }
        })

        targetListTasks.forEach((t, index) => {
            const isMovedTask = t.id === taskId
            if (t.order !== index || isMovedTask) {
                const taskRef = doc(db, 'tasks', t.id)
                const update: Record<string, any> = { order: index, updatedAt: serverTimestamp() }
                if (isMovedTask) {
                    // Moving to another list emits a task_moved activity event — stamp the
                    // real user and clear sticky connector markers so it isn't badged MCP/API.
                    update.listId = targetListId
                    update.updatedBy = authState.id
                    update.viaMcp = null
                    update.apiKeyName = null
                }
                updates.push(updateDoc(taskRef, update))
            }
        })

        yield call(() => Promise.all(updates))
        showToast('Task moved')
    } catch (err) {
        console.error('Error moving task to list:', err)
        showToast('Failed to move task. Please try again.', { type: 'error' })
    }
}

export function* toggleCompleteWorker(action: PayloadAction<{ taskId: string; isCompleted: boolean }>): Generator<any, void, any> {
    const { taskId, isCompleted } = action.payload

    const statuses = yield select(selectStatuses)
    const allTasks: Task[] = yield select(selectAllTasks)
    const task = allTasks.find((t: Task) => t.id === taskId)
    if (!task) return

    // Resolve where the task lands. Completing → the Done status. Un-completing → the status it
    // held before it was completed (task.previousStatusId), as long as it still exists and isn't
    // itself a Done status; otherwise fall back to Todo (SCD-982).
    let targetStatus: Status | undefined
    if (isCompleted) {
        targetStatus = statuses.find((s: Status) => s.systemType === 'done')
    } else {
        const restored = task.previousStatusId
            ? statuses.find((s: Status) => s.id === task.previousStatusId && s.systemType !== 'done')
            : undefined
        targetStatus = restored ?? statuses.find((s: Status) => s.systemType === 'todo')
    }
    if (!targetStatus) return

    // Remember the pre-completion status when completing so un-completing can restore it; clear it
    // on un-complete so a stale target can't linger.
    const previousStatusId: string | null = isCompleted ? task.statusId : null

    const authState = yield select(selectAuth)
    if (!authState) {
        yield put(updateTaskLocal({ taskId, data: { isCompleted, statusId: targetStatus.id, previousStatusId } }))
        return
    }

    const listTasks = allTasks.filter((t: Task) => t.listId === task.listId && t.id !== taskId)

    let newOrder: number
    if (isCompleted) {
        const completedOrders = listTasks.filter((t: Task) => t.isCompleted).map((t: Task) => t.order)
        newOrder = completedOrders.length > 0 ? Math.min(...completedOrders) - 1 : task.order
    } else {
        const activeOrders = listTasks.filter((t: Task) => !t.isCompleted).map((t: Task) => t.order)
        newOrder = activeOrders.length > 0 ? Math.max(...activeOrders) + 1 : task.order
    }

    // Optimistic local update BEFORE the write. The task is about to leave the
    // open-tasks listener's `isCompleted == false` query, firing a 'removed'
    // docChange → removeTaskIdsScoped({ completed: false }). That guard only keeps
    // the task if its local isCompleted already === true; without this optimistic
    // flip the task is deleted from the store for the gap until the completed-tasks
    // listener re-adds it. In that gap selectTaskIdByNumber returns undefined and the
    // open task modal bounces to "/" — the intermittent "complete → wrong section"
    // bug (SCD-696). Setting isCompleted now makes the removal guard a no-op.
    yield put(updateTaskLocal({ taskId, data: { isCompleted, statusId: targetStatus.id, order: newOrder, previousStatusId } }))

    try {
        const taskRef = doc(db, 'tasks', taskId)
        yield call(() => updateDoc(taskRef, {
            isCompleted,
            statusId: targetStatus.id,
            order: newOrder,
            previousStatusId,
            updatedAt: serverTimestamp(),
            updatedBy: authState.id,
            viaMcp: null,
            apiKeyName: null
        }))

        // Only a real open → completed transition counts (`task` was read before the
        // optimistic flip above); un-completing or re-completing a completed task doesn't.
        if (isCompleted && !task.isCompleted) {
            trackEvent(AnalyticsEvent.TaskCompleted, { workspace_id: task.workspaceId })
        }
    } catch (err) {
        console.error('Error toggling task complete:', err)
        // Roll back the optimistic flip so the UI doesn't show a stale completed state.
        yield put(updateTaskLocal({ taskId, data: { isCompleted: task.isCompleted, statusId: task.statusId, order: task.order, previousStatusId: task.previousStatusId ?? null } }))
    }
}

// Exported listener for workspace-level subscription

// Eager (cold-path) listener: OPEN tasks only. Completed tasks are the bulk of an
// aged workspace and aren't on the first-paint critical path, so loading only the
// open set here keeps the initial Firestore payload (which shares the long-polling
// channel with the projects snapshot that gates the loader) small. Completed tasks
// load via listenCompletedTasks after first paint. A completion toggle moves a task
// from this query to the completed one — see removeTaskIdsScoped for the race guard.
export function* listenAllTasks(workspaceId: string): Generator<any, void, any> {
    logTiming(`[tasks] listenAllTasks (open) started (workspaceId=${workspaceId})`)
    // Reset before the first snapshot so a workspace switch can't leave tasks from the
    // previous workspace in the store (incremental updates never "remove" them otherwise).
    yield put(clearTasks())
    const channel: ReturnType<typeof createSnapshotChannel> = yield call(
        createSnapshotChannel,
        query(collection(db, 'tasks'), where('workspaceId', '==', workspaceId), where('isCompleted', '==', false))
    )

    let firstData = true
    try {
        while (true) {
            const event: any = yield take(channel)
            if (event.type === 'data') {
                // Apply only what changed (added/modified/removed) instead of replacing the
                // whole store. On the first snapshot every doc is reported as "added".
                const changes = event.snapshot.docChanges()
                const upserts: Task[] = []
                const removedIds: string[] = []
                let hasAdded = false
                for (const change of changes) {
                    if (change.type === 'removed') {
                        removedIds.push(change.doc.id)
                    } else {
                        upserts.push({ id: change.doc.id, ...change.doc.data() } as Task)
                        if (change.type === 'added') hasAdded = true
                    }
                }
                // Always dispatch (even empty) so `initialized` flips to true on an empty workspace.
                yield put(upsertTasks(upserts))
                // Scoped removal: an id leaving the open query may be a delete OR a completion
                // (now owned by the completed listener) — only drop it if it's still open in store.
                if (removedIds.length > 0) yield put(removeTaskIdsScoped({ ids: removedIds, completed: false }))
                // Clear in-flight optimistic tasks only when real docs arrived (their saved
                // counterparts are now in the store); skip on modified/removed-only snapshots.
                if (hasAdded) yield put(clearOptimistic())
                yield put(setLoading(false))
                if (firstData) {
                    logTiming(`[tasks] first open snapshot (${event.snapshot.size} docs)`)
                    firstData = false
                }
            } else {
                yield put(setError('Failed to load tasks'))
                yield put(setLoading(false))
            }
        }
    } finally {
        channel.close()
    }
}

// Deferred listener: COMPLETED tasks only. Started after first paint (see
// startDeferredListeners) so the cold load isn't taxed by the completed bulk. It
// only adds to / removes from the store — it never clears it or touches the
// loading/initialized gate (the open listener owns those). The scoped removal
// guards the completion race symmetrically with the open listener.
export function* listenCompletedTasks(workspaceId: string): Generator<any, void, any> {
    logTiming(`[tasks] listenCompletedTasks started (workspaceId=${workspaceId})`)
    const channel: ReturnType<typeof createSnapshotChannel> = yield call(
        createSnapshotChannel,
        query(collection(db, 'tasks'), where('workspaceId', '==', workspaceId), where('isCompleted', '==', true))
    )

    let firstData = true
    try {
        while (true) {
            const event: any = yield take(channel)
            if (event.type === 'data') {
                const changes = event.snapshot.docChanges()
                const upserts: Task[] = []
                const removedIds: string[] = []
                for (const change of changes) {
                    if (change.type === 'removed') {
                        removedIds.push(change.doc.id)
                    } else {
                        upserts.push({ id: change.doc.id, ...change.doc.data() } as Task)
                    }
                }
                if (upserts.length > 0) yield put(upsertTasks(upserts))
                if (removedIds.length > 0) yield put(removeTaskIdsScoped({ ids: removedIds, completed: true }))
                if (firstData) {
                    yield put(setCompletedInitialized(true))
                    logTiming(`[tasks] first completed snapshot (${event.snapshot.size} docs)`)
                    firstData = false
                }
            } else {
                // Non-fatal: the open set already rendered; completed tasks just won't appear.
                console.error('[tasks] completed listener error')
            }
        }
    } finally {
        channel.close()
    }
}

// Per-task description listener — runs only while a task is open (TaskModal). Loads the
// lazily-stored description from task_descriptions/{taskId} and merges it into byId so the
// editor reads task.description as before. Stays subscribed for live updates.
function* listenTaskDescription(taskId: string): Generator<any, void, any> {
    const channel = yield call(createSnapshotChannel, doc(db, 'task_descriptions', taskId))
    try {
        while (true) {
            const event: any = yield take(channel)
            // Always signal load completion so the editor can mount once with final content
            // (no second async paint → no modal open-jank). A missing doc means "no
            // description yet" → empty array. (Safe post-migration: task docs no longer carry
            // a description field, so there's nothing to clobber.)
            if (event.type === 'data') {
                // A cache-only first snapshot for a doc that isn't cached reports exists=false
                // (empty), then the server snapshot lands with the real content. On a deep link the
                // open gate waits on the description, so writing that premature empty would open the
                // card without its body and pop the description in a beat later (SCD-903). Skip the
                // cache-miss; wait for a snapshot that's from the server or actually has the doc.
                if (!event.snapshot.exists() && event.snapshot.metadata?.fromCache) {
                    continue
                }
                const content = event.snapshot.exists() ? (event.snapshot.data().content ?? []) : []
                yield put(updateTaskLocal({ taskId, data: { description: content } as Partial<Task> }))
            } else {
                // On a read error still mark the description as "loaded" (empty) so the modal,
                // which waits for it before opening, never hangs.
                yield put(updateTaskLocal({ taskId, data: { description: [] } as Partial<Task> }))
            }
        }
    } finally {
        channel.close()
    }
}

function* descriptionSubscriptionWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const listener: SagaTask = yield fork(listenTaskDescription, action.payload)
    yield take(unsubscribeDescriptionSaga.type)
    yield cancel(listener)
}

// One-shot prefetch (e.g. on hover in the task list) so opening the task shows its description
// immediately instead of after a cold read.
// - Idle warming (no force): skips if already loaded, and never overwrites a value that arrived
//   meanwhile (e.g. the open modal's live listener).
// - Deliberate intent (force: true — hover/mousedown): re-reads from the server even when a value
//   is already cached, so a description changed after the tab loaded is refreshed before the modal
//   opens. A description edit never touches the task doc, so the eager task listener gives an open
//   tab no signal the body changed — without this the stale cached value would render, then the
//   modal's own listener would pop the fresh content in a beat later (SCD-927). getDoc here is from
//   the server, so it also warms the IndexedDB cache → the modal listener's first cache snapshot is
//   already fresh, killing the flicker too.
function* prefetchDescriptionWorker(action: PayloadAction<string | { taskId: string; force?: boolean }>): Generator<any, void, any> {
    const { taskId, force } = typeof action.payload === 'string'
        ? { taskId: action.payload, force: false }
        : action.payload
    const existing = yield select((state: any) => state.tasks.byId[taskId]?.description)
    if (existing !== undefined && !force) return
    try {
        const snap = yield call(getDoc, doc(db, 'task_descriptions', taskId))
        const content = snap.exists() ? (snap.data().content ?? []) : []
        // Idle warming must not clobber a value the open modal's live listener delivered while this
        // read was in flight. A forced refresh is the freshest server read, so it always writes.
        const current = yield select((state: any) => state.tasks.byId[taskId]?.description)
        if (force || current === undefined) {
            yield put(updateTaskLocal({ taskId, data: { description: content } as Partial<Task> }))
        }
    } catch {
        // Best-effort: if the prefetch fails, the modal's own listener still loads it.
    }
}

// Deep-link fast path (SCD-887): a /task/PREFIX-N link opens before the full task wave loads.
// Fetch just that one task by number, plus its project (so the modal's privacy check passes),
// and inject both into the store. The modal route opens as soon as task + project are present;
// the full listeners keep loading the rest in the background.
function* fetchTaskByNumberWorker(action: PayloadAction<{ workspaceId: string; number: number }>): Generator<any, void, any> {
    const { workspaceId, number } = action.payload
    try {
        // Two equality filters → no composite index needed (zigzag merge).
        const snap = yield call(() => getDocs(query(
            collection(db, 'tasks'),
            where('workspaceId', '==', workspaceId),
            where('number', '==', number),
            fsLimit(1)
        )))
        if (!snap.empty) {
            const d = snap.docs[0]
            const baseTask = { id: d.id, ...d.data() } as Task
            // Load the description (separate collection) and the project in parallel. The
            // description is fetched here — not left to the live subscription — so the deep-link
            // card opens COMPLETE (title/assignee/properties come with the task, the description
            // with it) instead of the body popping in after the window (SCD-903). The getDoc has a
            // [] fallback on missing/denied, so the open gate that waits on the description can
            // never hang (unlike the live subscription, which can be cancelled mid-load when the
            // workspace switches on a deep link). The project fetch lets selectProjectById resolve
            // (a permission-denied → private project, handled by the route's redirect).
            const [description, projectDoc] = yield call(() => Promise.all([
                getDoc(doc(db, 'task_descriptions', d.id))
                    .then((s) => (s.exists() ? (s.data()?.content ?? []) : []))
                    .catch(() => []),
                getDoc(doc(db, 'projects', baseTask.projectId))
                    .then((s) => (s.exists() ? ({ id: s.id, ...s.data() }) : null))
                    .catch(() => null),
            ]))
            // addTaskLocal (not upsertTasks) so it doesn't flip `initialized` — the full open-tasks
            // listener owns that. The listener merges its copy later (description stays intact).
            yield put(addTaskLocal({ ...baseTask, description } as Task))
            if (projectDoc) {
                yield put(upsertProject(projectDoc as Project))
            }
        }
    } catch (err) {
        console.error('[tasks] fetchTaskByNumber failed:', err)
    } finally {
        yield put(setDeepLinkFetchPending(false))
    }
}

export function* watchTasksSaga() {
    yield takeLatest(createTaskSaga.type, createTaskWorker)
    yield takeEvery(createTasksSaga.type, createTasksWorker)
    yield takeLatest(updateTaskSaga.type, updateTaskWorker)
    // takeEvery (not takeLatest) so a description save is never cancelled by a later task-doc
    // field update — that race silently dropped the body (SCD-905).
    yield takeEvery(updateTaskDescriptionSaga.type, updateTaskDescriptionWorker)
    yield takeLatest(deleteTaskSaga.type, deleteTaskWorker)
    yield takeEvery(reorderTaskSaga.type, reorderTaskWorker)
    yield takeEvery(moveTaskToListSaga.type, moveTaskToListWorker)
    yield takeLatest(toggleCompleteSaga.type, toggleCompleteWorker)
    yield takeLatest(subscribeDescriptionSaga.type, descriptionSubscriptionWorker)
    yield takeEvery(prefetchDescriptionSaga.type, prefetchDescriptionWorker)
    yield takeLatest(fetchTaskByNumberSaga.type, fetchTaskByNumberWorker)
}
